import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

export type DsTabButtonSize = 'sm' | 'md';

@Component({
  selector: 'ds-tab-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      class="ds-tab-button"
      type="button"
      role="tab"
      [class.ds-tab-button--selected]="selected && !disabled"
      [class.ds-tab-button--invert]="invert"
      [class.ds-tab-button--sm]="size === 'sm'"
      [class.ds-tab-button--md]="size === 'md'"
      [disabled]="disabled || null"
      [attr.aria-selected]="selected && !disabled"
      [attr.aria-label]="ariaLabel || null"
      (click)="handleClick()"
    >
      <span class="ds-tab-button__label ds-text--visual-padding-none">{{ label }}</span>
    </button>
  `,
  styles: [`
    :host {
      display: inline-flex;
      max-width: 100%;
    }

    .ds-tab-button {
      --_min-height: var(--ds-tab-button-md-min-height);
      --_padding-x: var(--ds-tab-button-md-padding-x);
      --_padding-y: var(--ds-tab-button-md-padding-y);
      --_font-size: var(--ds-tab-button-md-font-size);
      --_line-height: var(--ds-tab-button-md-line-height);
      --_background: var(--ds-tab-button-background);
      --_hover-background: var(--ds-tab-button-hover-background);
      --_selected-background: var(--ds-tab-button-selected-background);
      --_selected-border-color: var(--ds-tab-button-selected-border-color);
      --_selected-outline-color: var(--ds-tab-button-selected-outline-color);
      --_content-color: var(--ds-tab-button-content-color);
      --_selected-content-color: var(--ds-tab-button-selected-content-color);
      --_disabled-content-color: var(--ds-tab-button-disabled-content-color);
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      min-height: var(--_min-height);
      padding: var(--_padding-y) var(--_padding-x);
      border: var(--ds-tab-button-border-width) solid transparent;
      border-radius: var(--ds-tab-button-border-radius);
      background: var(--_background);
      box-sizing: border-box;
      color: var(--_content-color);
      cursor: pointer;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--_font-size);
      font-weight: var(--ds-tab-button-font-weight);
      line-height: var(--_line-height);
      outline: none;
      transition:
        background-color 120ms ease,
        border-color 120ms ease,
        box-shadow 120ms ease,
        opacity 120ms ease,
        color 120ms ease;
    }

    .ds-tab-button--sm {
      --_min-height: var(--ds-tab-button-sm-min-height);
      --_padding-x: var(--ds-tab-button-sm-padding-x);
      --_padding-y: var(--ds-tab-button-sm-padding-y);
      --_font-size: var(--ds-tab-button-sm-font-size);
      --_line-height: var(--ds-tab-button-sm-line-height);
    }

    .ds-tab-button--invert {
      --_background: var(--ds-tab-button-invert-background);
      --_hover-background: var(--ds-tab-button-invert-hover-background);
      --_selected-background: var(--ds-tab-button-invert-selected-background);
      --_selected-border-color: var(--ds-tab-button-invert-selected-border-color);
      --_selected-outline-color: var(--ds-tab-button-invert-selected-outline-color);
      --_content-color: var(--ds-tab-button-invert-content-color);
      --_selected-content-color: var(--ds-tab-button-invert-selected-content-color);
      --_disabled-content-color: var(--ds-tab-button-invert-disabled-content-color);
    }

    .ds-tab-button:hover:not(:disabled):not(.ds-tab-button--selected) {
      background: var(--_hover-background);
    }

    .ds-tab-button--selected {
      border-color: var(--_selected-border-color);
      background: var(--_selected-background);
      box-shadow: 0 0 0 var(--ds-tab-button-selected-outline-width) var(--_selected-outline-color);
      color: var(--_selected-content-color);
      font-weight: var(--ds-tab-button-selected-font-weight);
    }

    .ds-tab-button:focus-visible {
      box-shadow: none;
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-tab-button:disabled {
      background: var(--_background);
      color: var(--_disabled-content-color);
      cursor: not-allowed;
      opacity: calc(var(--ds-tab-button-disabled-opacity) / 100px);
    }

    .ds-tab-button__label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-text--visual-padding-none {
      text-box-trim: trim-both;
    }
  `],
})
export class DsTabButtonComponent {
  @Input() label = '';
  @Input() selected = false;
  @Input() disabled = false;
  @Input() invert = false;
  @Input() size: DsTabButtonSize = 'md';
  @Input() ariaLabel = '';

  @Output() clicked = new EventEmitter<void>();
  @Output() selectedChange = new EventEmitter<boolean>();

  handleClick(): void {
    if (this.disabled) return;
    this.clicked.emit();
    this.selectedChange.emit(true);
  }
}
